import React, { useState, useEffect, Suspense, lazy, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Loader2, AlertCircle, MapPin, Search, X, Mail, Phone,
  Shield, Award, Users, ChevronDown, ChevronUp, ArrowRight,
  Building2, Filter, CheckCircle2, Sparkles
} from 'lucide-react';
import { MapSettings, DEFAULT_MAP_SETTINGS } from '../components/MapWrapper';
import ServiceReviewCarousel from '../components/ServiceReviewCarousel';
import { isAuthenticated as isAssociateAuthenticated } from '../services/otpAuthService';
import CondividiStudio from '../components/CondividiStudio';
import { PARAM_STUDIO, studioDaQuery } from '../utils/condividiStudio';
import { useToast } from '../contexts/ToastContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const MapWrapper = lazy(() => import('../components/MapWrapper'));

interface CertifiedActivity {
  name: string;
  isCertified: 'SI' | 'NO' | 'PREAUTORIZZATO';
}

interface MemberLocation {
  id: string;
  businessName: string;
  address: string;
  citta?: string;
  indirizzo?: string;
  civico?: string;
  province: string;
  region: string;
  email?: string;
  phone?: string;
  certifiedActivities?: CertifiedActivity[];
  coordinates: { lat: number; lng: number };
  isSecondarySede?: boolean;
}

const CertifiedNetworkPublicPage: React.FC = () => {
  const [members, setMembers] = useState<MemberLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedActivity, setSelectedActivity] = useState('');
  const [showList, setShowList] = useState(false);
  const [showActivityFilter, setShowActivityFilter] = useState(false);
  const [mapSettings, setMapSettings] = useState<MapSettings>(DEFAULT_MAP_SETTINGS);
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const mapBoxRef = useRef<HTMLDivElement>(null);

  // Link profondo `?studio=<id>` (bottoni Copia/WhatsApp): mappa centrata con la scheda aperta
  const focusMemberId = studioDaQuery(searchParams.toString() ? `?${searchParams.toString()}` : '');

  const showCounter = mapSettings.showMemberCount || isAssociateAuthenticated();

  useEffect(() => {
    const fetchMapSettings = async () => {
      try {
        const r = await axios.get(`${API_BASE_URL}/public/settings/map`);
        if (r.data.success) setMapSettings(r.data.data);
      } catch {}
    };
    fetchMapSettings();
    fetchMapData();
  }, []);

  const fetchMapData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/public/certified-network/map`);
      if (response.data.success) setMembers(response.data.data.members);
      else setError('Errore nel caricamento dei dati');
    } catch {
      setError('Impossibile caricare i dati della mappa');
    } finally {
      setLoading(false);
    }
  };

  // Province uniche
  const provinces = useMemo(() =>
    Array.from(new Set(members.map(m => m.province))).filter(p => p?.trim()).sort()
  , [members]);

  // Città per provincia selezionata
  const cities = useMemo(() => {
    const base = selectedProvince ? members.filter(m => m.province === selectedProvince) : members;
    return Array.from(new Set(base.map(m => m.citta?.trim() || ''))).filter(Boolean).sort();
  }, [members, selectedProvince]);

  // Attività certificate uniche (da tutti i membri)
  const allActivities = useMemo(() => {
    const set = new Set<string>();
    members.forEach(m => {
      m.certifiedActivities?.forEach(a => {
        if (a.isCertified === 'SI' || a.isCertified === 'PREAUTORIZZATO') {
          set.add(a.name);
        }
      });
    });
    return Array.from(set).sort();
  }, [members]);

  // Membri filtrati
  const filteredMembers = useMemo(() => {
    let f = members;
    if (selectedProvince) f = f.filter(m => m.province === selectedProvince);
    if (selectedCity) f = f.filter(m => (m.citta?.trim() || '') === selectedCity);
    if (selectedActivity) {
      f = f.filter(m =>
        m.certifiedActivities?.some(
          a => a.name === selectedActivity &&
               (a.isCertified === 'SI' || a.isCertified === 'PREAUTORIZZATO')
        )
      );
    }
    return f;
  }, [members, selectedProvince, selectedCity, selectedActivity]);

  useEffect(() => { setSelectedCity(''); }, [selectedProvince]);

  // Studio del link: porta la mappa in vista; se non c'e' piu', lo dice e toglie il parametro
  const focusDone = useRef<string | null>(null);
  useEffect(() => {
    if (loading || !focusMemberId || focusDone.current === focusMemberId) return;
    focusDone.current = focusMemberId;
    if (members.some(m => m.id === focusMemberId)) {
      mapBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      toast.warning('Lo studio del link non è più presente nella Rete Certificata');
      setSearchParams({}, { replace: true });
    }
  }, [loading, members, focusMemberId, toast, setSearchParams]);

  // Cambiando i filtri il link profondo ha fatto il suo lavoro: via il parametro
  const filtersTouched = useRef(false);
  useEffect(() => {
    if (!filtersTouched.current) { filtersTouched.current = true; return; }
    if (searchParams.has(PARAM_STUDIO)) setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvince, selectedCity, selectedActivity]);

  const italyCenter: [number, number] = [41.8719, 12.5674];
  const initialZoom = mapSettings.defaultZoom || 6;
  const hasFilters = selectedProvince !== '' || selectedCity !== '' || selectedActivity !== '';
  const resetFilters = () => { setSelectedProvince(''); setSelectedCity(''); setSelectedActivity(''); };

  return (
    <>
      <Helmet>
        <title>Trova uno studio certificato UNASCA — Rete Certificata</title>
        <meta name="description" content="Cerca gli studi di consulenza automobilistica certificati UNASCA nella tua città. Pratiche auto, moto, nautiche. Qualità verificata in tutta Italia." />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">

        {/* ═══ HERO ═══ */}
        <section className="relative overflow-hidden bg-primary-950 text-white">
          <div className="absolute inset-y-0 right-0 w-full lg:w-[60%]">
            <img
              src="/images/rete-certificata-pagina/ingresso-studio.webp"
              srcSet="/images/rete-certificata-pagina/ingresso-studio-1280.webp 1280w, /images/rete-certificata-pagina/ingresso-studio.webp 2400w"
              sizes="(min-width: 1024px) 60vw, 100vw"
              alt=""
              aria-hidden="true"
              fetchPriority="high"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-primary-950/80 lg:bg-transparent lg:bg-gradient-to-r lg:from-primary-950 lg:from-[40%] lg:via-primary-950/70 lg:via-[55%] lg:to-transparent" />

          <div className="relative container-custom pt-14 pb-32 md:pt-20 md:pb-36">
            <div className="max-w-xl motion-safe:animate-fade-in">

              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur-sm mb-6">
                <Sparkles className="w-4 h-4 text-primary-300" aria-hidden="true" />
                <span className="text-sm font-semibold text-white">
                  Rete Certificata UNASCA
                </span>
              </div>

              <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-5 leading-tight tracking-tight [text-wrap:balance]">
                Trova lo studio certificato vicino a te
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-xl mb-0">
                Professionisti verificati per pratiche auto, moto e nautiche.
                Qualità garantita UNASCA in tutta Italia.
              </p>

            </div>
          </div>

          <div className="pointer-events-none absolute bottom-36 right-10 hidden -rotate-6 text-right text-white lg:block xl:right-16" aria-hidden="true">
            <span className="block text-4xl leading-tight drop-shadow-lg" style={{ fontFamily: "'Caveat', cursive" }}>
              Qualità verificata,<br />vicino a te
            </span>
            <svg viewBox="0 0 220 16" className="ml-auto mt-1 w-52 text-primary-400" fill="none">
              <path d="M3 12 C 60 2, 140 2, 217 8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>
        </section>

        {/* ═══ PANNELLO FILTRI ═══ */}
        <div className="container-custom -mt-20 mb-0 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 p-4 sm:p-6">

              {/* Riga 1: Provincia + Città (sempre full-width su mobile) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                {/* Provincia */}
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600 pointer-events-none" />
                  <select
                    value={selectedProvince}
                    onChange={e => setSelectedProvince(e.target.value)}
                    className="w-full pl-9 pr-10 py-2.5 text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none cursor-pointer"
                  >
                    <option value="">Tutte le province</option>
                    {provinces.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>

                {/* Città */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-600 pointer-events-none" />
                  <select
                    value={selectedCity}
                    onChange={e => setSelectedCity(e.target.value)}
                    disabled={!selectedProvince}
                    className="w-full pl-9 pr-10 py-2.5 text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <option value="">Tutte le città</option>
                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Riga 2: Tipo pratica + Reset */}
              <div className="flex flex-wrap gap-2 mb-1">
                {/* Toggle filtro attività */}
                <button
                  onClick={() => setShowActivityFilter(v => !v)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                    selectedActivity || showActivityFilter
                      ? 'bg-green-600 text-white border-green-600 shadow-sm'
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-green-400 hover:text-green-600'
                  }`}
                >
                  <Filter className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate max-w-[160px] sm:max-w-none">
                    {selectedActivity ? selectedActivity : 'Tipo pratica'}
                  </span>
                  {selectedActivity && (
                    <span
                      onClick={e => { e.stopPropagation(); setSelectedActivity(''); }}
                      className="ml-1 hover:opacity-70 flex-shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </span>
                  )}
                </button>

                {/* Reset */}
                {hasFilters && (
                  <button
                    onClick={resetFilters}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-600"
                  >
                    <X className="w-4 h-4" />
                    Reset
                  </button>
                )}
              </div>

              {/* Riga 2: Pill attività (collassabile) */}
              {showActivityFilter && allActivities.length > 0 && (
                <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                    Filtra per tipo di pratica
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {allActivities.map(activity => (
                      <button
                        key={activity}
                        onClick={() => {
                          setSelectedActivity(prev => prev === activity ? '' : activity);
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          selectedActivity === activity
                            ? 'bg-green-600 text-white border-green-600 shadow-sm'
                            : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-green-400 hover:text-green-700 dark:hover:text-green-400'
                        }`}
                      >
                        {selectedActivity === activity && <CheckCircle2 className="w-3 h-3" />}
                        {activity}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Risultati */}
              {!loading && (
                <div className="flex flex-wrap items-center justify-between gap-y-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {hasFilters ? (
                      <>
                        <span className="font-semibold text-green-600 dark:text-green-400 text-sm">
                          {filteredMembers.length}
                        </span> studi trovati
                        {selectedProvince && <> in <strong>{selectedProvince}</strong></>}
                        {selectedCity && <> · <strong>{selectedCity}</strong></>}
                        {selectedActivity && <> · <strong>{selectedActivity}</strong></>}
                      </>
                    ) : showCounter ? (
                      <>
                        <span className="font-semibold text-green-600 dark:text-green-400 text-sm">
                          {members.length}
                        </span> studi certificati attivi in tutta Italia
                      </>
                    ) : null}
                  </p>
                  <button
                    onClick={() => setShowList(v => !v)}
                    className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                  >
                    {showList ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {showList ? 'Nascondi elenco' : 'Mostra elenco studi'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══ ELENCO STUDI (collassabile) ═══ */}
        {showList && !loading && (
          <div className="container-custom mt-4">
            <div className="max-w-5xl mx-auto">
              {filteredMembers.length === 0 ? (
                <div className="card p-8 text-center">
                  <MapPin className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nessuno studio per i filtri selezionati.
                  </p>
                  <button onClick={resetFilters}
                    className="mt-3 text-xs text-green-600 dark:text-green-400 underline underline-offset-2">
                    Rimuovi filtri
                  </button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-1 pb-1">
                  {filteredMembers.map(member => (
                    <div key={member.id} className="card-hover p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug line-clamp-2">
                          {member.businessName}
                        </h4>
                        <span className={`flex-shrink-0 text-2xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                          member.isSecondarySede
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                        }`}>
                          {member.isSecondarySede ? 'Secondaria' : 'Principale'}
                        </span>
                      </div>
                      <div className="space-y-1 mb-3">
                        <div className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-2">{member.address}</span>
                        </div>
                        {member.phone && (
                          <div className="flex items-center gap-1.5 text-xs">
                            <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <a href={`tel:${member.phone}`}
                              className="text-green-600 dark:text-green-400 hover:underline truncate">
                              {member.phone}
                            </a>
                          </div>
                        )}
                        {member.email && (
                          <div className="flex items-center gap-1.5 text-xs">
                            <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <a href={`mailto:${member.email}`}
                              className="text-green-600 dark:text-green-400 hover:underline truncate">
                              {member.email}
                            </a>
                          </div>
                        )}
                      </div>
                      {/* Attività certificate */}
                      {member.certifiedActivities && member.certifiedActivities.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                          {member.certifiedActivities.slice(0, 3).map(a => (
                            <span key={a.name}
                              className={`text-2xs font-medium px-1.5 py-0.5 rounded ${
                                selectedActivity === a.name
                                  ? 'bg-green-600 text-white'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}>
                              {a.name}
                            </span>
                          ))}
                          {member.certifiedActivities.length > 3 && (
                            <span className="text-2xs text-gray-400 dark:text-gray-500 px-1.5 py-0.5">
                              +{member.certifiedActivities.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                      <CondividiStudio studio={member} variant="card" className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ MAPPA ═══ */}
        <div className="container-custom mt-6 mb-0" style={{ isolation: 'isolate' }}>
          <div className="max-w-5xl mx-auto">
            <div ref={mapBoxRef} className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden ring-1 ring-black/5 dark:ring-white/10">
              {loading ? (
                <div className="h-[320px] sm:h-[460px] md:h-[560px] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                  <div className="text-center">
                    <Loader2 className="w-10 h-10 text-green-600 animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Caricamento mappa…</p>
                  </div>
                </div>
              ) : error ? (
                <div className="h-[320px] sm:h-[460px] md:h-[560px] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                  <div className="text-center px-4">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{error}</p>
                    <button onClick={fetchMapData}
                      className="btn btn-primary btn-small">
                      Riprova
                    </button>
                  </div>
                </div>
              ) : (
                <Suspense fallback={
                  <div className="h-[320px] sm:h-[460px] md:h-[560px] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                  </div>
                }>
                  {filteredMembers.length > 0 ? (
                    <div className="h-[320px] sm:h-[460px] md:h-[560px]">
                      <MapWrapper
                        key={`map-${selectedProvince}-${selectedCity}-${selectedActivity}-${mapSettings.enableClustering}-${mapSettings.markerIconType}`}
                        members={filteredMembers}
                        center={italyCenter}
                        zoom={initialZoom}
                        settings={mapSettings}
                        fitToMarkers={hasFilters}
                        enableShare
                        focusMemberId={focusMemberId}
                        height="100%"
                      />
                    </div>
                  ) : (
                    <div className="h-[320px] sm:h-[460px] md:h-[560px] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                      <div className="text-center px-4">
                        <MapPin className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                          {members.length === 0
                            ? 'Nessuno studio disponibile'
                            : 'Nessun risultato per i filtri selezionati'}
                        </p>
                        {hasFilters && (
                          <button onClick={resetFilters} className="btn btn-secondary btn-small">
                            Rimuovi filtri
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </Suspense>
              )}
            </div>

            {/* Legenda marker */}
            <div className="flex items-center gap-6 mt-3 px-1">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-green-500 border-2 border-white shadow-sm" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Sede principale</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-sm" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Sede secondaria</span>
              </div>
              {hasFilters && showCounter && (
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
                  {filteredMembers.length} su {members.length} studi
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ═══ COSA GARANTISCE ═══ */}
        <section className="py-16 mt-8">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Cosa garantisce la certificazione?
                </h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm max-w-md mx-auto">
                  Un marchio riconosciuto, standard professionali verificati e tutela reale per il cittadino.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-5">
                {[
                  {
                    icon: <Shield className="w-6 h-6 text-white" />,
                    title: 'Qualità verificata',
                    desc: 'GDPR, cybersecurity, assicurazione professionale e formazione continua: ogni studio supera controlli rigorosi prima di essere certificato.'
                  },
                  {
                    icon: <Award className="w-6 h-6 text-white" />,
                    title: 'Certificato di Stato',
                    desc: 'Il certificato è rilasciato con il patrocinio del Poligrafico e Zecca dello Stato: un riconoscimento ufficiale, non un semplice bollino.'
                  },
                  {
                    icon: <Users className="w-6 h-6 text-white" />,
                    title: 'Rete nazionale',
                    desc: 'Studi collegati in tutta Italia per garantire continuità di servizio e la possibilità di delega tra professionisti certificati.'
                  }
                ].map(({ icon, title, desc }) => (
                  <div key={title} className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
                    <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-primary-50 dark:bg-primary-900/20" aria-hidden="true" />
                    <div className="relative w-12 h-12 bg-primary-700 rounded-full flex items-center justify-center mb-4 shadow">
                      {icon}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ═══ BANNER ASSOCIATI ═══ */}
        <section className="relative overflow-hidden bg-gradient-to-r from-primary-950 via-primary-900 to-primary-800">
          <div
            className="absolute inset-0 opacity-10 text-primary-300"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }}
            aria-hidden="true"
          />
          <div className="relative container-custom py-12">
            <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-green-200" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-green-200">
                    Per gli studi UNASCA
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-1">
                  Vuoi portare il tuo studio in rete?
                </h3>
                <p className="text-sm text-green-100 max-w-sm">
                  Scopri vantaggi, requisiti e FAQ. Oltre 2.800 studi hanno già scelto la certificazione UNASCA.
                </p>
              </div>
              <div className="flex flex-col xs:flex-row gap-3 flex-shrink-0 w-full sm:w-auto">
                <Link
                  to="/rete-certificata/studi"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-white/15 hover:bg-white/25 text-white border border-white/30 transition-colors"
                >
                  Scopri di più
                </Link>
                <Link
                  to="/rete-certificata/iscrizione"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-white text-green-700 hover:bg-green-50 transition-colors shadow-sm"
                >
                  Iscriviti ora
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Recensioni Servizi */}
        <ServiceReviewCarousel />

      </div>
    </>
  );
};

export default CertifiedNetworkPublicPage;
